import { takeLatest, put, call, all, fork } from 'redux-saga/effects';
import BranchTypes from './branch.types';
import {
	createBranch,
	updateBranch,
	getBranchesByOrganisation,
	getBranchById,
} from './branch.services';
import {
	createBranchSuccess,
	createBranchFailure,
	updateBranchSuccess,
	updateBranchFailure,
	getBranchesByOrganisationSuccess,
	getBranchesByOrganisationFailure,
	getBranchByIdSuccess,
	getBranchByIdFailure,
} from './branch.action';
import AlertManager from '../../utils/alertManager';
import { encryptData } from '../_common/enode-decode';

// Create Branch Saga
export function* createBranchSaga(payload: any) {
	try {
		if (process.env.NODE_ENV === 'development') console.log('createBranchSaga called with payload:', payload);
		const encryptedData = encryptData(payload.payload, BranchTypes.CREATE_BRANCH_KEY);
		// @ts-ignore
		const responseData = yield call(createBranch as any, encryptedData);
		if (process.env.NODE_ENV === 'development') console.log('createBranchSaga response:', responseData);
		if (responseData.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			if (responseData.data.status === 'success') {
				yield put(createBranchSuccess(responseData.data));
			} else {
				yield put(createBranchFailure(responseData.data));
			}
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('createBranchSaga error:', error);
		yield put(createBranchFailure(error?.response?.data?.message || 'An error occurred'));
	}
}

// Update Branch Saga
export function* updateBranchSaga(payload: any) {
	try {
		if (process.env.NODE_ENV === 'development') console.log('updateBranchSaga called with payload:', payload);
		const encryptedData = encryptData(payload.payload, BranchTypes.UPDATE_BRANCH_KEY);
		// @ts-ignore
		const responseData = yield call(updateBranch as any, encryptedData);
		if (process.env.NODE_ENV === 'development') console.log('updateBranchSaga response:', responseData);
		if (responseData.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			if (responseData.data.status === 'success') {
				yield put(updateBranchSuccess(responseData.data));
			} else {
				yield put(updateBranchFailure(responseData.data));
			}
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('updateBranchSaga error:', error);
		yield put(updateBranchFailure(error?.response?.data?.message || 'An error occurred'));
	}
}

// Get Branches By Organisation Saga
export function* getBranchesByOrganisationSaga(payload: any) {
	try {
		if (process.env.NODE_ENV === 'development') console.log('getBranchesByOrganisationSaga called with payload:', payload);
		const encryptedData = encryptData(
			payload.payload,
			BranchTypes.GET_BRANCHES_BY_ORGANISATION_KEY,
		);
		// @ts-ignore
		const responseData = yield call(getBranchesByOrganisation as any, encryptedData);
		if (process.env.NODE_ENV === 'development') console.log('getBranchesByOrganisationSaga response:', responseData);
		if (responseData.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			if (responseData.data.status === 'success') {
				yield put(getBranchesByOrganisationSuccess(responseData.data));
			} else {
				yield put(getBranchesByOrganisationFailure(responseData.data));
			}
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('getBranchesByOrganisationSaga error:', error);
		yield put(
			getBranchesByOrganisationFailure(error?.response?.data?.message || 'An error occurred'),
		);
	}
}

export function* getBranchByIdSaga(payload: any) {
	try {
		const key = 'getBranchByIdKey';
		const encryptedData = encryptData(payload.payload, key);
		if (process.env.NODE_ENV === 'development') console.log('=== GET BRANCH BY ID SAGA ===');
		if (process.env.NODE_ENV === 'development') console.log('getBranchByIdSaga called with payload:', payload);
		if (process.env.NODE_ENV === 'development') console.log('payload.payload:', payload.payload);

		// @ts-ignore
		const responseData = yield call(getBranchById as any, encryptedData);
		if (process.env.NODE_ENV === 'development') console.log('getBranchByIdSaga response:', responseData);
		if (process.env.NODE_ENV === 'development') console.log('responseData.data:', responseData.data);
		if (process.env.NODE_ENV === 'development') console.log('responseData.data.data:', responseData.data?.data);
		if (process.env.NODE_ENV === 'development') console.log('responseData.data.status:', responseData.data?.status);
		if (process.env.NODE_ENV === 'development') console.log('responseData.data.message:', responseData.data?.message);
		if (responseData.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			if (responseData.data.status === 'success') {
				// Check if the branch data is nested under data.data or directly in data
				const branchData = responseData.data.data || responseData.data;
				if (process.env.NODE_ENV === 'development') console.log('Final branch data to send to reducer:', branchData);
				if (process.env.NODE_ENV === 'development') console.log('Branch data type:', typeof branchData);
				if (process.env.NODE_ENV === 'development') console.log('Branch data keys:', Object.keys(branchData));
				yield put(getBranchByIdSuccess(branchData));
			} else {
				if (process.env.NODE_ENV === 'development') console.log('API returned error status:', responseData.data);
				yield put(getBranchByIdFailure(responseData.data));
			}
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('getBranchByIdSaga error:', error);
		if (process.env.NODE_ENV === 'development') console.error('Error response:', error?.response);
		if (process.env.NODE_ENV === 'development') console.error('Error message:', error?.message);
		yield put(getBranchByIdFailure(error?.response?.data?.message || 'An error occurred'));
	}
}

// Watcher Sagas
export function* onCreateBranchStart(): Generator<any, void, any> {
	if (process.env.NODE_ENV === 'development') console.log('onCreateBranchStart watcher registered');
	yield takeLatest(BranchTypes.CREATE_BRANCH_START as any, createBranchSaga);
}

export function* onUpdateBranchStart(): Generator<any, void, any> {
	if (process.env.NODE_ENV === 'development') console.log('onUpdateBranchStart watcher registered');
	yield takeLatest(BranchTypes.UPDATE_BRANCH_START as any, updateBranchSaga);
}

export function* onGetBranchesByOrganisationStart(): Generator<any, void, any> {
	if (process.env.NODE_ENV === 'development') console.log('onGetBranchesByOrganisationStart watcher registered');
	yield takeLatest(
		BranchTypes.GET_BRANCHES_BY_ORGANISATION_START as any,
		getBranchesByOrganisationSaga,
	);
}

export function* onGetBranchByIdStart(): Generator<any, void, any> {
	if (process.env.NODE_ENV === 'development') console.log('onGetBranchByIdStart watcher registered');
	yield takeLatest(BranchTypes.GET_BRANCH_BY_ID_START as any, getBranchByIdSaga);
}

// Main Saga
export function* branchSagaDetails(): Generator<any, void, any> {
	if (process.env.NODE_ENV === 'development') console.log('branchSagaDetails started');
	yield all([
		fork(onCreateBranchStart),
		fork(onUpdateBranchStart),
		fork(onGetBranchesByOrganisationStart),
		fork(onGetBranchByIdStart),
	]);
	if (process.env.NODE_ENV === 'development') console.log('branchSagaDetails completed');
}
